/**
 * WMS page UDA scope — the dropdown that sits above every WMS (New) screen.
 *
 * Why a page-level control rather than the global header selector: every UDA tenant
 * shares one schema, so WMS data is scoped by a WHERE clause on uda_id, not by the
 * connection (see server utils/uda-scope.utils.js). Switching UDA in the global header
 * re-issues the whole session's tenant, which a system admin should not have to do just
 * to read another authority's dashboard. So the pick lives here and rides on every WMS
 * request as the `x-wms-uda` header, which resolveUdaScope honours above the JWT.
 *
 * ONE pick covers the module: choose a UDA on the dashboard and the works list, the
 * setup masters, the reports and every create form are already on that UDA — they are
 * all reading the same store, and the header goes out on all of their requests.
 *
 * "All UDAs" (null) sends no scope, which is the state-level rollup. It is a reading
 * view: a WMS record has to belong to exactly one authority, so a create issued from it
 * is refused by the server with a message saying to pick one. The bar says so first.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import baseAPIService from '@/services/baseapiservice';

/**
 * localStorage key for the persisted pick. The axios interceptor reads this key
 * DIRECTLY rather than importing the store — this module reaches the auth context, which
 * reaches the interceptor, so an import the other way would close a cycle. Any change to
 * the key or the shape has to be made in both places (see services/interceptor.ts).
 */
export const WMS_UDA_STORAGE_KEY = 'wms-uda-scope';

export interface WmsUdaOption {
  uda_id: number;
  uda_cd?: string;
  uda_nm: string;
}

interface WmsUdaScopeState {
  /** ds_uda_lst_t.uda_id, or null for "All UDAs". */
  udaId: number | null;
  setUdaId: (udaId: number | null) => void;
}

export const useWmsUdaScopeStore = create<WmsUdaScopeState>()(
  persist(
    (set) => ({
      udaId: null,
      setUdaId: (udaId) => set({ udaId }),
    }),
    { name: WMS_UDA_STORAGE_KEY }
  )
);

/**
 * How many header-tone dropdowns are mounted. A page that carries the picker in its own
 * banner registers here, and WmsUdaScopeBar leaves its strip off while any is mounted —
 * so the strip only appears on screens that have no header picker (setup, masters,
 * reports), with no route list to keep in step with the pages.
 */
const useHeaderPickerStore = create<{ count: number; add: (n: number) => void }>()((set) => ({
  count: 0,
  add: (n) => set((s) => ({ count: Math.max(0, s.count + n) })),
}));

/** Session cache for the UDA master — fixed list, asked for by every WMS screen. */
let udaCache: WmsUdaOption[] | null = null;
let udaPromise: Promise<WmsUdaOption[]> | null = null;

/** The UDA master (cached per session). Exported for pickers outside the scope bar,
 *  e.g. Project Configuration's UDA filter. */
export async function fetchWmsUdas(): Promise<WmsUdaOption[]> {
  return fetchUdas();
}

async function fetchUdas(): Promise<WmsUdaOption[]> {
  if (udaCache) return udaCache;
  if (!udaPromise) {
    udaPromise = (async () => {
      const res = await baseAPIService.get('erp-admin/uda');
      if (res.data?.status !== 200) throw new Error(res.data?.message || 'Failed to fetch UDAs');
      const list: WmsUdaOption[] = (res.data?.data || [])
        .filter((u: any) => u?.uda_id != null)
        .map((u: any) => ({
          uda_id: Number(u.uda_id),
          uda_cd: u.uda_cd,
          uda_nm: u.uda_nm || u.uda_cd || String(u.uda_id),
        }))
        .sort((a: WmsUdaOption, b: WmsUdaOption) => a.uda_nm.localeCompare(b.uda_nm));
      udaCache = list;
      return list;
    })().catch((err) => { udaPromise = null; throw err; });
  }
  return udaPromise;
}

export interface WmsUdaScope {
  /** True when the acting user may re-scope — their JWT carries no UDA of its own. */
  canChoose: boolean;
  /** The scope in force: a uda_id, or null for the all-UDAs rollup. */
  udaId: number | null;
  /** True while no single UDA is in force — WMS writes are refused in that state. */
  isAllUdas: boolean;
  udas: WmsUdaOption[];
  loading: boolean;
  /** Display name of the UDA in force (or the pinned user's own). */
  label: string;
  setUdaId: (udaId: number | null) => void;
}

/**
 * The UDA a WMS screen is working on. Safe to call from any WMS page or form —
 * the create forms use it to show, and to require, the authority they file into.
 */
export function useWmsUdaScope(): WmsUdaScope {
  const { user: authUser } = useAuth();
  const ownUdaId = (authUser as any)?.udaId ?? null;
  const canChoose = (authUser as any)?.isStateRegionalUser === true || ownUdaId === null;

  const picked = useWmsUdaScopeStore((s) => s.udaId);
  const setUdaId = useWmsUdaScopeStore((s) => s.setUdaId);

  const [udas, setUdas] = useState<WmsUdaOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUdas()
      .then((list) => { if (!cancelled) setUdas(list); })
      .catch((error) => { if (!cancelled) { console.error('Error fetching UDAs:', error); setUdas([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // A pinned user cannot re-scope: the server takes their UDA from the token whatever
  // the dropdown says, so report that one rather than a pick they cannot act on.
  const udaId = canChoose ? picked : (ownUdaId != null ? Number(ownUdaId) : null);

  const label = useMemo(() => {
    if (udaId == null) return 'All UDAs';
    return udas.find((u) => u.uda_id === udaId)?.uda_nm || `UDA #${udaId}`;
  }, [udaId, udas]);

  return { canChoose, udaId, isAllUdas: udaId == null, udas, loading, label, setUdaId };
}

interface WmsUdaSelectProps {
  /** Compact form for a page header strip; the default suits a form field. */
  compact?: boolean;
  /**
   * 'dark' renders for a coloured modal header (the create forms), where the page's
   * own scope strip is covered by the dialog and the operator still has to see — and
   * be able to change — the authority the record is about to be filed into.
   * 'header' is a rounded glass pill for a page's own gradient banner (the WMS
   * dashboard), matching the view-mode and filter pills beside it.
   */
  tone?: 'light' | 'dark' | 'header';
  className?: string;
}

/** The dropdown itself. Read-only for a user the server pins to one UDA. */
export function WmsUdaSelect({ compact = true, tone = 'light', className }: WmsUdaSelectProps) {
  const { canChoose, udaId, udas, loading, label, setUdaId } = useWmsUdaScope();
  const addHeaderPicker = useHeaderPickerStore((s) => s.add);

  // Layout effect, so the strip is gone before the page first paints with its own picker.
  useLayoutEffect(() => {
    if (tone !== 'header') return undefined;
    addHeaderPicker(1);
    return () => addHeaderPicker(-1);
  }, [tone, addHeaderPicker]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setUdaId(e.target.value === '' ? null : Number(e.target.value));
    },
    [setUdaId]
  );

  const onDark = tone === 'dark';
  const isAll = udaId == null;

  if (tone === 'header') {
    const pill = 'inline-flex h-7 items-center gap-1 rounded-full border border-white/25 bg-white/15 p-0.5 backdrop-blur-sm transition';
    const chip = (
      <span className="flex h-full flex-shrink-0 items-center gap-1 rounded-full bg-white px-2 text-[10px] font-bold uppercase tracking-wider text-teal-700 shadow-sm">
        <i className="ri-government-line text-xs"></i>UDA
      </span>
    );

    if (!canChoose) {
      return (
        <span className={className ?? pill} title="Your UDA — set by your login">
          {chip}
          <span className="flex items-center gap-1.5 pl-1 pr-2.5 text-[11px] font-semibold text-white">
            <span className="max-w-[200px] truncate">{label}</span>
            <i className="ri-lock-line text-[11px] text-white/60"></i>
          </span>
        </span>
      );
    }

    return (
      <div
        className={className ?? `group ${pill} hover:border-white/40 hover:bg-white/25 focus-within:border-white/60 focus-within:ring-2 focus-within:ring-white/25`}
      >
        {chip}
        <div className="relative flex h-full items-center">
          <i className={`pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-xs leading-none text-white/80 ${
            isAll ? 'ri-global-line' : 'ri-building-2-line'
          }`}></i>
          <select
            value={udaId ?? ''}
            disabled={loading}
            onChange={handleChange}
            title={`Scope every WMS screen to one UDA — ${label}`}
            className="m-0 block h-6 w-[150px] cursor-pointer appearance-none truncate border-0 bg-transparent py-0 pl-6 pr-6 align-middle text-[11px] font-semibold leading-6 text-white outline-none focus-visible:outline-none disabled:cursor-wait sm:w-[200px]"
          >
            <option value="" className="text-slate-800">{loading ? 'Loading UDAs...' : 'All UDAs'}</option>
            {udas.map((u) => (
              <option key={u.uda_id} value={u.uda_id} className="text-slate-800">{u.uda_nm}</option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-sm leading-none text-white/80 transition-transform group-focus-within:rotate-180"></i>
        </div>
      </div>
    );
  }

  /* One segmented pill: a fixed "UDA" tag on the left, the picker on the right. The old
   * control was an 11px select with a loose label beside it — easy to miss for the one
   * setting that decides what every WMS screen shows. Sizes: `compact` for the page
   * strip and the create-form headers, full width otherwise (a form field). */
  const shell = onDark
    ? 'border-white/25 bg-white/10 hover:bg-white/15 focus-within:border-white/60 focus-within:ring-2 focus-within:ring-white/20'
    : 'border-slate-200 bg-white shadow-sm hover:border-indigo-300 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100';
  const tag = onDark
    ? 'border-white/20 bg-white/10 text-white/80'
    : 'border-slate-200 bg-slate-50 text-slate-500';
  const height = compact ? 'h-7' : 'h-9';

  if (!canChoose) {
    return (
      <span
        className={className ?? `inline-flex ${height} items-center overflow-hidden rounded-lg border ${shell}`}
        title="Your UDA — set by your login"
      >
        <span className={`flex h-full items-center gap-1.5 border-r px-2.5 text-[11px] font-semibold uppercase tracking-wide ${tag}`}>
          <i className="ri-government-line text-sm"></i>UDA
        </span>
        <span className={`flex items-center gap-1.5 px-3 text-xs font-semibold ${onDark ? 'text-white' : 'text-slate-800'}`}>
          <span className="truncate max-w-[220px]">{label}</span>
          <i className={`ri-lock-line text-[11px] ${onDark ? 'text-white/60' : 'text-slate-400'}`}></i>
        </span>
      </span>
    );
  }

  return (
    <div
      className={className ?? `group inline-flex ${height} items-center overflow-hidden rounded-lg border transition ${shell} ${compact ? '' : 'w-full'}`}
    >
      <span className={`flex h-full flex-shrink-0 items-center gap-1.5 border-r px-2.5 text-[11px] font-semibold uppercase tracking-wide ${tag}`}>
        <i className="ri-government-line text-sm"></i>UDA
      </span>
      <div className={`relative h-full ${compact ? '' : 'flex-1'}`}>
        {/* Globe for the all-UDAs rollup, a building once one authority is picked. */}
        <i className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm ${
          isAll ? 'ri-global-line' : 'ri-building-2-line'
        } ${onDark ? 'text-white/70' : isAll ? 'text-slate-400' : 'text-indigo-500'}`}></i>
        <select
          value={udaId ?? ''}
          disabled={loading}
          onChange={handleChange}
          title="Scope every WMS screen to one UDA"
          className={`h-full appearance-none truncate bg-transparent pl-8 pr-8 font-semibold outline-none focus-visible:outline-none disabled:cursor-wait cursor-pointer ${
            onDark ? 'text-white' : 'text-slate-800'
          } ${compact ? `${onDark ? 'w-[210px]' : 'w-[260px]'} text-xs` : 'w-full text-sm'}`}
        >
          <option value="" className="text-slate-800">{loading ? 'Loading UDAs...' : 'All UDAs'}</option>
          {udas.map((u) => (
            <option key={u.uda_id} value={u.uda_id} className="text-slate-800">{u.uda_nm}</option>
          ))}
        </select>
        <i className={`ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base transition-transform group-focus-within:rotate-180 ${
          onDark ? 'text-white/70' : 'text-slate-400 group-hover:text-indigo-500'
        }`}></i>
      </div>
    </div>
  );
}

/**
 * The strip that carries the dropdown, rendered once as a pathless layout route so it
 * appears above EVERY WMS screen — dashboards, the transaction lists, setup and reports —
 * without each page having to host its own copy. A page whose banner carries the
 * header-tone picker suppresses the strip (see useHeaderPickerStore).
 */
export function WmsUdaScopeBar() {
  const { udaId } = useWmsUdaScope();
  // The report screens carry the picker the report data obeys in their own masthead,
  // and x-wms-uda never reaches the report endpoints, so the strip stays off there.
  const onReports = useLocation().pathname.startsWith('/app/ds-wms/reports');
  const showStrip = useHeaderPickerStore((s) => s.count) === 0 && !onReports;

  return (
    <>
      {/* No "pick one to create" note here any more: the create forms carry their own
          UDA picker, and the server refuses a create made from "All UDAs" with a message
          saying so. */}
      {showStrip && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-white px-4 py-1">
          <WmsUdaSelect />
        </div>
      )}
      {/*
        Keyed on the scope so changing UDA REMOUNTS the page below.
        The pick travels as a request header, not as a prop or a query param, so nothing
        on the page depends on it in React's eyes: a dashboard that fetched in
        useEffect([]) would keep showing the UDA it loaded with, and the dropdown would
        look broken even though every subsequent request was correctly scoped. Remounting
        re-runs those effects against the new scope. Same device Layout uses when the
        session's tenant changes.
      */}
      <Outlet key={udaId ?? 'all-udas'} />
    </>
  );
}

export default WmsUdaScopeBar;
